import { BillEorFooterResponse } from '../models/api/view/claim/financials/bills/bill-eor-footer-response';
import { BillEorHeaderResponse } from '../models/api/view/claim/financials/bills/bill-eor-header-response';

/**
 * Utility for transforming EOR (Explanation of Review) data
 * Maps complex EOR response data into simplified structures
 * Note: Date formatting is handled by FormatPipe in the UI layer
 */
export class EorDataMapper {
  /**
   * Format city, state, and zip into a comma-separated string
   * @param city The city name
   * @param state The state name or abbreviation
   * @param zip The zip/postal code
   * @returns Formatted string with non-empty parts joined by ', '
   */
  static formatCityStateZip(city: string, state: string, zip: string): string {
    return [city, state, zip].filter((p) => p && p.trim()).join(', ');
  }

  /**
   * Format city, state, country, and postal code into a comma-separated string
   * @param city The city name
   * @param state The state name or abbreviation
   * @param country The country name
   * @param postal The postal/zip code
   * @returns Formatted string with non-empty parts joined by ', '
   */
  static formatCityStateCountryPostal(
    city: string,
    state: string,
    country: string,
    postal: string,
  ): string {
    return [city, state, country, postal]
      .filter((p) => p && p.trim())
      .join(', ');
  }
  /**
   * Map EOR payor data for display
   * Date values are returned as-is for FormatPipe to handle
   * @param claimNumber The TPA claim number to display
   */
  static mapPayorData(
    billEorHeader: BillEorHeaderResponse | null,
    billItem: any,
    claimNumber = '',
  ): any {
    if (!billEorHeader && !billItem) return null;

    const vendorRcvdDate = billEorHeader?.vendorRcvdDate || '';
    const vendorProcDate = billEorHeader?.vendorProcDate || '';
    const injuryDate = billEorHeader?.injuryDate || '';
    const icdCodes = billEorHeader?.icdCodes || '';
    const payor = (billEorHeader as any)?.payor || '';

    // Format service date (DOS) - combine dosBegin and dosEnd if both exist
    let serviceDate = '';
    if (billItem?.dosBegin && billItem?.dosEnd) {
      serviceDate =
        billItem.dosBegin === billItem.dosEnd
          ? billItem.dosBegin
          : `${billItem.dosBegin} - ${billItem.dosEnd}`;
    } else if (billItem?.dosBegin) {
      serviceDate = billItem.dosBegin;
    } else if (billItem?.dosEnd) {
      serviceDate = billItem.dosEnd;
    }

    return {
      payor: payor,
      tpaClaimNum: claimNumber,
      billIcn: billItem?.icn || '',
      billDcn: billItem?.dcn || '',
      sirDcn: billItem?.sirDcn || '',
      serviceDate: serviceDate,
      vendorRcvdDate: vendorRcvdDate,
      vendorProcDate: vendorProcDate,
      injuryDate: injuryDate,
      icdCodes: icdCodes,
    };
  }

  /**
   * Map EOR injured party data for display
   */
  static mapInjuredData(billEorHeader: BillEorHeaderResponse | null): any {
    if (!billEorHeader) return null;

    const {
      clmntFirstName,
      clmntLastName,
      ssn,
      patientAccount,
      clmntPhone,
      clmntAddr1,
      clmntAddr2,
      clmntCity,
      clmntState,
      clmntZip,
    } = billEorHeader;

    // Format claimant city/state/zip
    const clmntCityStZip = EorDataMapper.formatCityStateZip(
      clmntCity,
      clmntState,
      clmntZip,
    );

    return {
      fullName: `${clmntLastName || ''}, ${clmntFirstName || ''}`
        .trim()
        .replace(/^,\s*|,\s*$/g, ''),
      ssn: ssn,
      patientAccount: patientAccount,
      phone: clmntPhone,
      address: [clmntAddr1, clmntAddr2, clmntCityStZip]
        .filter((x) => x)
        .join(', '),
      address1: clmntAddr1,
      address2: clmntAddr2,
      city: clmntCity,
      state: clmntState,
      zip: clmntZip,
    };
  }

  /**
   * Map EOR employer data for display
   */
  static mapEmployerData(
    billEorHeader: BillEorHeaderResponse | null,
    billItem: any,
  ): any {
    if (!billEorHeader) return null;

    const {
      contractAddr1,
      contractAddr2,
      contractCity,
      contractState,
      contractZip,
    } = billEorHeader;

    // Format contract city/state/zip
    const contractCityStZip = EorDataMapper.formatCityStateZip(
      contractCity,
      contractState,
      contractZip,
    );

    return {
      employer: billItem?.contractName || '',
      contractNumber: billItem?.contractNumber || '',
      address: [contractAddr1, contractAddr2, contractCityStZip]
        .filter((x) => x)
        .join(', '),
      address1: contractAddr1,
      address2: contractAddr2,
      city: contractCity,
      state: contractState,
      zip: contractZip,
    };
  }

  /**
   * Map EOR carrier data for display
   */
  static mapCarrierData(billEorHeader: BillEorHeaderResponse | null): any {
    if (!billEorHeader) return null;

    const {
      carrierName,
      carrierPhone,
      carrierAddr1,
      carrierAddr2,
      carrierCity,
      carrierState,
      carrierZip,
    } = billEorHeader;

    // Format carrier city/state/zip
    const carrierCityStZip = EorDataMapper.formatCityStateZip(
      carrierCity,
      carrierState,
      carrierZip,
    );

    return {
      carrierName: carrierName,
      carrierPhone: carrierPhone,
      address: [carrierAddr1, carrierAddr2, carrierCityStZip]
        .filter((x) => x)
        .join(', '),
      address1: carrierAddr1,
      address2: carrierAddr2,
      city: carrierCity,
      state: carrierState,
      zip: carrierZip,
    };
  }

  /**
   * Map EOR provider data for display
   * Provider name comes from billEorHeader.provName (the provider/facility)
   * Treating provider name comes from billItem.treatingProvider (the individual)
   */
  static mapProviderData(
    billEorHeader: BillEorHeaderResponse | null,
    billItem: any,
  ): any {
    if (!billEorHeader && !billItem) return null;

    const provName = billEorHeader?.provName || '';
    const provTaxId = billEorHeader?.provTaxId || '';
    const provNpi = billEorHeader?.provNpi || '';
    const provPhone = billEorHeader?.provPhone || '';
    const provAddr1 = billEorHeader?.provAddr1 || '';
    const provAddr2 = billEorHeader?.provAddr2 || '';
    const provCity = billEorHeader?.provCity || '';
    const provState = billEorHeader?.provState || '';
    const provZip = billEorHeader?.provZip || '';

    // Format provider city/state/zip
    const provCityStZip = EorDataMapper.formatCityStateZip(
      provCity,
      provState,
      provZip,
    );

    return {
      provider: provName,
      treatingProvider: billItem?.treatingProvider || '',
      provTaxId: provTaxId,
      provNpi: provNpi,
      provPhone: provPhone,
      address: [provAddr1, provAddr2, provCityStZip]
        .filter((x) => x)
        .join(', '),
      address1: provAddr1,
      address2: provAddr2,
      city: provCity,
      state: provState,
      zip: provZip,
    };
  }

  /**
   * Map EOR footer vendor data for display
   */
  static mapFooterVendorData(billEorFooter: BillEorFooterResponse | null): any {
    if (!billEorFooter) return null;

    const {
      vendorName,
      pqPerson,
      pqVendorName,
      networkID,
      subNetwork,
      pqPhone,
      pqFax,
      vendorAddress1,
      vendorAddress2,
      vendorCity,
      vendorState,
      vendorCountry,
      vendorZip,
    } = billEorFooter;

    // Format vendor address3 using the new city/state/country/zip fields
    const vendorAddress3 = EorDataMapper.formatCityStateCountryPostal(
      vendorCity,
      vendorState,
      vendorCountry,
      vendorZip,
    );

    return {
      vendorName: vendorName,
      pqPerson: pqPerson,
      pqVendorName: pqVendorName,
      networkID: networkID,
      subNetwork: subNetwork,
      pqPhone: pqPhone,
      pqFax: pqFax,
      address: [vendorAddress1, vendorAddress2, vendorAddress3]
        .filter((x) => x)
        .join(', '),
      address1: vendorAddress1,
      address2: vendorAddress2,
      city: vendorCity,
      state: vendorState,
      country: vendorCountry,
      zip: vendorZip,
    };
  }

  /**
   * Map EOR footer reconsideration data for display
   */
  static mapFooterReconsiderationData(
    billEorFooter: BillEorFooterResponse | null,
  ): any {
    if (!billEorFooter) return null;

    const {
      reconName,
      reconPhone,
      reconFax,
      reconAddress1,
      reconAddress2,
      reconCity,
      reconState,
      reconZip,
    } = billEorFooter;

    // Format reconCityStateZip using the new city/state/zip fields
    const reconCityStateZip = EorDataMapper.formatCityStateZip(
      reconCity,
      reconState,
      reconZip,
    );

    return {
      reconName: reconName,
      reconPhone: reconPhone,
      reconFax: reconFax,
      address: [reconAddress1, reconAddress2, reconCityStateZip]
        .filter((x) => x)
        .join(', '),
      address1: reconAddress1,
      address2: reconAddress2,
      city: reconCity,
      state: reconState,
      zip: reconZip,
    };
  }
}
